import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';

type AcronymStyle = 'professional' | 'creative' | 'technical';

const professionalWords = {
  A: ['Advanced', 'Agile', 'Active'],
  B: ['Business', 'Better', 'Best'],
  C: ['Core', 'Creative', 'Collaborative'],
  D: ['Dynamic', 'Digital', 'Direct'],
  E: ['Effective', 'Efficient', 'Enhanced'],
  F: ['Future', 'Fast', 'Focused'],
  G: ['Global', 'Growth', 'Great'],
  H: ['High', 'Holistic', 'Helpful'],
  I: ['Innovative', 'Integrated', 'Intelligent'],
  J: ['Joint', 'Just', 'Judicious'],
  K: ['Key', 'Knowledge', 'Keen'],
  L: ['Leading', 'Learning', 'Logical'],
  M: ['Modern', 'Managed', 'Mobile'],
  N: ['New', 'Network', 'Next'],
  O: ['Optimal', 'Organized', 'Open'],
  P: ['Professional', 'Proactive', 'Progressive'],
  Q: ['Quality', 'Quick', 'Qualified'],
  R: ['Reliable', 'Responsive', 'Rapid'],
  S: ['Strategic', 'Smart', 'Sustainable'],
  T: ['Technical', 'Trusted', 'Transformative'],
  U: ['Universal', 'United', 'Ultimate'],
  V: ['Value', 'Virtual', 'Verified'],
  W: ['World', 'Web', 'Wise'],
  X: ['Excellence', 'Expert', 'Extended'],
  Y: ['Yield', 'Year', 'Young'],
  Z: ['Zero', 'Zenith', 'Zone']
};

const creativeWords = {
  A: ['Awesome', 'Artistic', 'Amazing'],
  B: ['Brilliant', 'Bold', 'Bright'],
  C: ['Creative', 'Cosmic', 'Colorful'],
  D: ['Dream', 'Dazzling', 'Divine'],
  E: ['Energetic', 'Epic', 'Exciting'],
  F: ['Fantastic', 'Fun', 'Fresh'],
  G: ['Genius', 'Glowing', 'Grand'],
  H: ['Happy', 'Harmony', 'Heart'],
  I: ['Inspiring', 'Infinite', 'Imaginative'],
  J: ['Joy', 'Journey', 'Jubilant'],
  K: ['Kind', 'Kinetic', 'Keen'],
  L: ['Love', 'Light', 'Lively'],
  M: ['Magic', 'Miracle', 'Mighty'],
  N: ['Natural', 'Novel', 'Noble'],
  O: ['Original', 'Ocean', 'Optimistic'],
  P: ['Perfect', 'Positive', 'Peaceful'],
  Q: ['Quest', 'Quality', 'Quantum'],
  R: ['Radiant', 'Rainbow', 'Rhythm'],
  S: ['Sparkle', 'Spirit', 'Soul'],
  T: ['True', 'Treasure', 'Triumph'],
  U: ['Unique', 'Unity', 'Ultra'],
  V: ['Vibrant', 'Vision', 'Vital'],
  W: ['Wonder', 'Wisdom', 'Wild'],
  X: ['Xtra', 'Xcel', 'Xplore'],
  Y: ['Youth', 'Yes', 'Yummy'],
  Z: ['Zest', 'Zen', 'Zoom']
};

const technicalWords = {
  A: ['Automated', 'Adaptive', 'Analytical'],
  B: ['Binary', 'Blockchain', 'Bandwidth'],
  C: ['Cloud', 'Cyber', 'Computing'],
  D: ['Data', 'Digital', 'Development'],
  E: ['Enterprise', 'Encryption', 'Engineering'],
  F: ['Framework', 'Function', 'Fiber'],
  G: ['Gateway', 'Grid', 'Generation'],
  H: ['Hardware', 'Hybrid', 'Host'],
  I: ['Interface', 'Integration', 'Infrastructure'],
  J: ['Java', 'Junction', 'JSON'],
  K: ['Kernel', 'Key', 'Knowledge'],
  L: ['Logic', 'Layer', 'Linear'],
  M: ['Machine', 'Micro', 'Memory'],
  N: ['Network', 'Node', 'Neural'],
  O: ['Operating', 'Object', 'Optical'],
  P: ['Protocol', 'Platform', 'Processing'],
  Q: ['Query', 'Quantum', 'Quality'],
  R: ['Router', 'Runtime', 'Resource'],
  S: ['System', 'Software', 'Security'],
  T: ['Technology', 'Terminal', 'Transfer'],
  U: ['Unit', 'Utility', 'Update'],
  V: ['Virtual', 'Vector', 'Version'],
  W: ['Web', 'Wireless', 'Workflow'],
  X: ['XML', 'XPath', 'XScale'],
  Y: ['Yield', 'YAML', 'Year'],
  Z: ['Zero', 'Zone', 'Zip']
};

const generateAcronyms = (letters: string, style: AcronymStyle): string[] => {
  const wordSet = style === 'professional' ? professionalWords :
                 style === 'creative' ? creativeWords :
                 technicalWords;

  const acronyms: string[] = [];
  const upperLetters = letters.toUpperCase();

  for (let i = 0; i < 5; i++) {
    const words: string[] = [];
    for (const letter of upperLetters) {
      if (wordSet[letter]) {
        const options = wordSet[letter];
        words.push(options[Math.floor(Math.random() * options.length)]);
      } else {
        words.push('...');
      }
    }
    acronyms.push(words.join(' '));
  }

  return acronyms;
};

const AcronymNameGenerator: React.FC = () => {
  const [letters, setLetters] = useState('');
  const [style, setStyle] = useState<AcronymStyle>('professional');
  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  const handleGenerate = () => {
    if (letters.trim()) {
      const names = generateAcronyms(letters, style);
      setGeneratedNames(names);
    }
  };

  const toggleFavorite = (name: string) => {
    setFavorites(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  return (
    <section id="acronym-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered Acronym Generation
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Acronym Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter Letters
              </label>
              <input
                type="text"
                value={letters}
                onChange={(e) => setLetters(e.target.value.replace(/[^A-Za-z]/g, ''))}
                placeholder="e.g., TEAM"
                maxLength={10}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as AcronymStyle)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="professional">Professional</option>
                <option value="creative">Creative</option>
                <option value="technical">Technical</option>
              </select>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              disabled={!letters.trim()}
              className={`bg-blue-600 text-white py-4 px-8 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 ${!letters.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Wand2 className="w-5 h-5" />
              Generate Acronyms
            </button>
          </div>

          {generatedNames.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Acronyms:
              </h3>
              <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto">
                {generatedNames.map((name, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
                  >
                    <span className="text-lg">{name}</span>
                    <button
                      onClick={() => toggleFavorite(name)}
                      className={`ml-4 p-2 rounded-full transition-colors ${
                        favorites.includes(name)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                    >
                      ★
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Click the star icon to save your favorite acronyms!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AcronymNameGenerator;